import React, { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useMpConfig, useMpBlocks, useMpStats, useMpRequests, useConstituencies, useCampaigns } from '../hooks/queries';
import { api } from '../lib/api';
import { hasFullAccess } from '../lib/permissions';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { 
  Plus, Search, Filter, Download, ChevronLeft, ChevronRight, 
  Edit2, CheckCircle2, History, X, AlertCircle, ClipboardList,
  Users, Building2, DollarSign, FileText, Briefcase, Home, BarChart3, TrendingUp, PieChart, Trash2, Phone
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  Legend, Cell, PieChart as RechartsPieChart, Pie, LineChart, Line, AreaChart, Area 
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Color palette for charts
const CHART_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];
const STATUS_COLORS = { Completed: '#10B981', Pending: '#F59E0B' };

// Stable identity for "no data yet". A `= []` default in a destructure creates a
// new array on every render; when that array is an effect dependency the effect
// re-runs forever.
const EMPTY = [];

export default function MPRequestsPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [viewMode, setViewMode] = useState('list');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    entry_type: 'all',
    request_type: '',
    category: '',
    status: '',
    company: '',
    block: '',
    constituency_id: '',
    campaign_id: '',
    date_from: '',
    date_to: '',
    sort_by: 'created_at',
    sort_order: 'desc'
  });
  const [showFilters, setShowFilters] = useState(false);

  const { data: config = { companies: [], request_types: [], issue_categories: [] } } = useMpConfig();
  const { data: blocks = [] } = useMpBlocks();
  const { data: stats } = useMpStats();
  const { data: constituencies = [] } = useConstituencies();
  const filterParams = Object.fromEntries(
    Object.entries(filters).filter(([, v]) => v != null && v !== '' && v !== 'all')
  );
  const { data: mpRequestsData, isLoading: loading } = useMpRequests({
    page,
    page_size: pageSize,
    ...filterParams,
  });

  const records = mpRequestsData?.records ?? [];
  const totalPages = mpRequestsData?.total_pages ?? 1;
  const total = mpRequestsData?.total ?? 0;

  const invalidateMp = () => {
    queryClient.invalidateQueries({ queryKey: ['mp'] });
  };
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [createType, setCreateType] = useState('request'); // 'request' or 'issue'
  const [editingRecord, setEditingRecord] = useState(null);
  const [historyRecords, setHistoryRecords] = useState([]);
  
  // Create form
  const [newRequest, setNewRequest] = useState({
    constituent_id: '',
    request_type: '',
    amount: '',
    company: '',
    current_designation: '',
    requested_designation: '',
    remarks: '',
    status: 'Pending',
    contact_number: ''
  });
  const [newIssue, setNewIssue] = useState({
    constituent_id: '',
    category: '',
    description: '',
    remarks: '',
    status: 'Pending',
    contact_number: ''
  });
  
  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // null for all, or record_id
  
  const [selectedConstituencyId, setSelectedConstituencyId] = useState('');
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  
  // Constituent search
  const [constituentSearch, setConstituentSearch] = useState('');
  const [constituentResults, setConstituentResults] = useState([]);
  const [selectedConstituent, setSelectedConstituent] = useState(null);
  const [searchingConstituent, setSearchingConstituent] = useState(false);

  const filterCampaigns = useCampaigns(filters.constituency_id || null).data ?? EMPTY;

  // campaignsData is the single source of truth — mirroring it into local state
  // via setCampaigns() in this effect is what caused the infinite render loop.
  const { data: campaignsData = EMPTY } = useCampaigns(selectedConstituencyId);
  useEffect(() => {
    if (campaignsData.length > 0 && !selectedCampaignId) {
      const sorted = [...campaignsData].sort((a, b) => {
        const dateA = a.campaign_date ? new Date(a.campaign_date) : new Date(0);
        const dateB = b.campaign_date ? new Date(b.campaign_date) : new Date(0);
        return dateB - dateA;
      });
      setSelectedCampaignId(sorted[0].campaign_id);
    } else if (campaignsData.length === 0) {
      setSelectedCampaignId('');
    }
    // selectedCampaignId intentionally excluded: we only set it when empty
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignsData, selectedConstituencyId]);

  const searchConstituents = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setConstituentResults([]);
      return;
    }
    if (!selectedCampaignId) {
      setConstituentResults([]);
      return;
    }
    setSearchingConstituent(true);
    try {
      const response = await api.get(
        `${API}/mp/constituents/search?search=${encodeURIComponent(query)}&campaign_id=${selectedCampaignId}`
      );
      setConstituentResults(response.data);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearchingConstituent(false);
    }
  }, [selectedCampaignId]);

  useEffect(() => {
    if (constituentSearch && selectedCampaignId) {
      searchConstituents(constituentSearch);
    } else {
      setConstituentResults([]);
    }
  }, [constituentSearch, selectedCampaignId, searchConstituents]);

  const selectConstituent = (constituent) => {
    setSelectedConstituent(constituent);
    setConstituentSearch(constituent.name);
    setConstituentResults([]);
    // Auto-fill contact number from constituent record
    const contactNumber = constituent.contact_number || '';
    if (createType === 'request') {
      setNewRequest({ ...newRequest, constituent_id: constituent.record_id, contact_number: contactNumber });
    } else {
      setNewIssue({ ...newIssue, constituent_id: constituent.record_id, contact_number: contactNumber });
    }
  };

  const handleCreateRequest = async () => {
    if (!selectedConstituencyId) {
      toast.error('Please select a constituency');
      return;
    }
    if (!selectedCampaignId) {
      toast.error('No campaign found for this constituency');
      return;
    }
    if (!selectedConstituent) {
      toast.error('Please select a constituent');
      return;
    }
    if (!newRequest.request_type) {
      toast.error('Please select a request type');
      return;
    }
    
    try {
      const payload = {
        constituent_id: newRequest.constituent_id,
        request_type: newRequest.request_type,
        status: newRequest.status,
        remarks: newRequest.remarks || null,
        contact_number: newRequest.contact_number || null
      };
      
      // Add amount for financial requests
      if (['NSPA', 'Utilities Water', 'Utilities Electric', 'Other Donation'].includes(newRequest.request_type)) {
        payload.amount = newRequest.amount ? parseFloat(newRequest.amount) : null;
      }
      
      // Add company for job/promotion
      if (['New Job', 'Promotion'].includes(newRequest.request_type)) {
        payload.company = newRequest.company || null;
      }
      
      // Add designation for promotion
      if (newRequest.request_type === 'Promotion') {
        payload.current_designation = newRequest.current_designation || null;
        payload.requested_designation = newRequest.requested_designation || null;
      }
      
      await api.post(`${API}/mp/requests`, payload, );
      toast.success('Request created successfully');
      setShowCreateDialog(false);
      resetForm();
      invalidateMp();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create request');
    }
  };

  const handleCreateIssue = async () => {
    if (!selectedConstituencyId) {
      toast.error('Please select a constituency');
      return;
    }
    if (!selectedCampaignId) {
      toast.error('No campaign found for this constituency');
      return;
    }
    if (!selectedConstituent) {
      toast.error('Please select a constituent');
      return;
    }
    if (!newIssue.category) {
      toast.error('Please select an issue category');
      return;
    }
    
    try {
      await api.post(`${API}/mp/issues`, {
        constituent_id: newIssue.constituent_id,
        category: newIssue.category,
        description: newIssue.description || null,
        remarks: newIssue.remarks || null,
        status: newIssue.status,
        contact_number: newIssue.contact_number || null
      }, );
      toast.success('Issue created successfully');
      setShowCreateDialog(false);
      resetForm();
      invalidateMp();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create issue');
    }
  };

  const handleUpdateRecord = async () => {
    if (!editingRecord) return;
    
    try {
      const isRequest = editingRecord.entry_type === 'request';
      const endpoint = isRequest 
        ? `${API}/mp/requests/${editingRecord.request_id}`
        : `${API}/mp/issues/${editingRecord.request_id}`;
      
      await api.put(endpoint, editingRecord, );
      toast.success('Record updated successfully');
      setShowEditDialog(false);
      setEditingRecord(null);
      invalidateMp();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update record');
    }
  };

  const handleQuickStatusUpdate = async (recordId, newStatus) => {
    try {
      await api.patch(`${API}/mp/requests/${recordId}/status?status=${newStatus}`, {}, );
      toast.success(`Status updated to ${newStatus}`);
      invalidateMp();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleViewHistory = async (record) => {
    try {
      const response = await api.get(`${API}/mp/requests/${record.request_id}/history`, );
      setHistoryRecords(response.data);
      setShowHistoryDialog(true);
    } catch (error) {
      toast.error('Failed to load history');
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.entry_type && filters.entry_type !== 'all') params.append('entry_type', filters.entry_type);
      if (filters.request_type) params.append('request_type', filters.request_type);
      if (filters.category) params.append('category', filters.category);
      if (filters.status) params.append('status', filters.status);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      
      const response = await api.get(`${API}/mp/export?${params.toString()}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mp_requests_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const handleDeleteRecord = async (recordId) => {
    try {
      await api.delete(`${API}/mp/requests/${recordId}`, );
      toast.success('Record deleted');
      invalidateMp();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleClearAll = async () => {
    try {
      const response = await api.delete(`${API}/mp/requests/all`, );
      toast.success(`Deleted ${response.data.deleted_count} records`);
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
      invalidateMp();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const confirmDelete = (target) => {
    setDeleteTarget(target);
    setShowDeleteConfirm(true);
  };

  const executeDelete = async () => {
    if (deleteTarget === 'all') {
      await handleClearAll();
    } else if (deleteTarget) {
      await handleDeleteRecord(deleteTarget);
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
    }
  };

  const resetForm = () => {
    setNewRequest({
      constituent_id: '',
      request_type: '',
      amount: '',
      company: '',
      current_designation: '',
      requested_designation: '',
      remarks: '',
      status: 'Pending',
      contact_number: ''
    });
    setNewIssue({
      constituent_id: '',
      category: '',
      description: '',
      remarks: '',
      status: 'Pending',
      contact_number: ''
    });
    setSelectedConstituent(null);
    setConstituentSearch('');
    setConstituentResults([]);
    setSelectedConstituencyId('');
    setSelectedCampaignId('');
  };

  const openCreateDialog = (type) => {
    setCreateType(type);
    resetForm();
    setShowCreateDialog(true);
  };

  const openEditDialog = (record) => {
    setEditingRecord({ ...record });
    setShowEditDialog(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount) => {
    if (!amount) return '-';
    return `MVR ${parseFloat(amount).toLocaleString()}`;
  };

  const getTypeLabel = (record) => {
    if (record.entry_type === 'issue') {
      return record.category || 'Issue';
    }
    return record.request_type || 'Request';
  };

  const getTypeColor = (record) => {
    if (record.entry_type === 'issue') {
      return 'bg-purple-100 text-purple-800';
    }
    const colors = {
      'Aasandha': 'bg-blue-100 text-blue-800',
      'NSPA': 'bg-green-100 text-green-800',
      'Utilities Water': 'bg-cyan-100 text-cyan-800',
      'Utilities Electric': 'bg-yellow-100 text-yellow-800',
      'Other Donation': 'bg-pink-100 text-pink-800',
      'New Job': 'bg-indigo-100 text-indigo-800',
      'Promotion': 'bg-orange-100 text-orange-800'
    };
    return colors[record.request_type] || 'bg-gray-100 text-gray-800';
  };

  const canEdit = hasFullAccess(user) || user?.effective_permissions?.includes('mp_requests.edit');
  const canClose = hasFullAccess(user) || user?.effective_permissions?.includes('mp_requests.close');
  const canExport = hasFullAccess(user) || user?.effective_permissions?.includes('mp_requests.export');
  const canCreate = hasFullAccess(user) || user?.effective_permissions?.includes('mp_requests.create');
  const canDelete = hasFullAccess(user) || user?.effective_permissions?.includes('mp_requests.delete');

  return (
    <Layout>
      <div className="p-4 md:p-6 max-w-[1600px] mx-auto">
        {/* Header - Mobile Optimized */}
        <div className="flex flex-col gap-4 mb-6">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-foreground">MP Requests & Issues</h1>
            <p className="text-sm text-muted-foreground">Track and manage constituent requests</p>
          </div>
          
          {/* Mobile: Pending/Completed Toggle */}
          <div className="flex md:hidden bg-muted rounded-lg p-1 w-full">
            <Button
              size="sm"
              variant={filters.status === '' ? 'default' : 'ghost'}
              onClick={() => setFilters({ ...filters, status: '' })}
              className="flex-1"
              data-testid="all-filter-mobile"
            >
              All ({total})
            </Button>
            <Button
              size="sm"
              variant={filters.status === 'Pending' ? 'default' : 'ghost'}
              onClick={() => setFilters({ ...filters, status: 'Pending' })}
              className="flex-1"
              data-testid="pending-filter-mobile"
            >
              Pending
            </Button>
            <Button
              size="sm"
              variant={filters.status === 'Completed' ? 'default' : 'ghost'}
              onClick={() => setFilters({ ...filters, status: 'Completed' })}
              className="flex-1"
              data-testid="completed-filter-mobile"
            >
              Done
            </Button>
          </div>

          {/* Action Buttons - Responsive */}
          <div className="flex flex-wrap items-center gap-2">
            {/* View Mode Toggle - Hidden on mobile */}
            <div className="hidden md:flex bg-muted rounded-lg p-1">
              <Button
                size="sm"
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                onClick={() => setViewMode('list')}
                className="px-3"
                data-testid="list-view-btn"
              >
                <ClipboardList className="h-4 w-4 mr-1" />
                List
              </Button>
              <Button
                size="sm"
                variant={viewMode === 'analytics' ? 'default' : 'ghost'}
                onClick={() => setViewMode('analytics')}
                className="px-3"
                data-testid="analytics-view-btn"
              >
                <BarChart3 className="h-4 w-4 mr-1" />
                Analytics
              </Button>
            </div>
            {canDelete && total > 0 && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => confirmDelete('all')} 
                className="text-red-600 hover:bg-red-50 hidden md:flex"
                data-testid="clear-all-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Clear All
              </Button>
            )}
            {canExport && (
              <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-btn" className="hidden md:flex">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            )}
            {canCreate && (
              <>
                <Button 
                  variant="outline" 
                  size="default"
                  onClick={() => openCreateDialog('issue')} 
                  data-testid="new-issue-btn"
                  className="flex-1 md:flex-none h-12 md:h-10"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Issue
                </Button>
                <Button 
                  size="default"
                  onClick={() => openCreateDialog('request')} 
                  data-testid="new-request-btn"
                  className="flex-1 md:flex-none h-12 md:h-10"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Request
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <ClipboardList className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="text-xs text-blue-700">Total Requests</p>
                    <p className="text-2xl font-bold text-blue-900">{stats.total_requests}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-purple-50 to-purple-100">
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-purple-600" />
                  <div>
                    <p className="text-xs text-purple-700">Total Issues</p>
                    <p className="text-2xl font-bold text-purple-900">{stats.total_issues}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-orange-50 to-orange-100">
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-orange-600" />
                  <div>
                    <p className="text-xs text-orange-700">Pending Requests</p>
                    <p className="text-2xl font-bold text-orange-900">{stats.pending_requests}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100">
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Home className="h-5 w-5 text-yellow-600" />
                  <div>
                    <p className="text-xs text-yellow-700">Pending Issues</p>
                    <p className="text-2xl font-bold text-yellow-900">{stats.pending_issues}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-green-50 to-green-100">
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="text-xs text-green-700">Completed</p>
                    <p className="text-2xl font-bold text-green-900">{stats.completed_requests + stats.completed_issues}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100">
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-indigo-600" />
                  <div>
                    <p className="text-xs text-indigo-700">Job/Promotion</p>
                    <p className="text-2xl font-bold text-indigo-900">
                      {stats.by_request_type?.filter(r => ['New Job', 'Promotion'].includes(r.type)).reduce((a, b) => a + b.count, 0) || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Analytics View */}
        {viewMode === 'analytics' && stats && (
          <div className="space-y-6">
            {/* Monthly Trends Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Monthly Trends
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stats.monthly_trends?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={stats.monthly_trends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Area type="monotone" dataKey="requests" stackId="1" stroke="#3B82F6" fill="#3B82F6" name="Requests" />
                      <Area type="monotone" dataKey="issues" stackId="1" stroke="#8B5CF6" fill="#8B5CF6" name="Issues" />
                      <Area type="monotone" dataKey="completed" stackId="2" stroke="#10B981" fill="#10B981" name="Completed" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-center text-muted-foreground py-8">No data available yet</p>
                )}
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Request Type Breakdown with Completion Rate */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PieChart className="h-5 w-5" />
                    Request Types & Completion Rate
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {stats.type_status_breakdown?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={stats.type_status_breakdown} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="type" type="category" width={100} tick={{ fontSize: 12 }} />
                        <Tooltip 
                          formatter={(value, name) => [value, name === 'completed' ? 'Completed' : 'Pending']}
                          labelFormatter={(label) => `Type: ${label}`}
                        />
                        <Legend />
                        <Bar dataKey="completed" stackId="a" fill="#10B981" name="Completed" />
                        <Bar dataKey="pending" stackId="a" fill="#F59E0B" name="Pending" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">No request data yet</p>
                  )}
                  {/* Completion rates list */}
                  {stats.type_status_breakdown?.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-sm font-medium mb-2">Completion Rates:</p>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {stats.type_status_breakdown.map((item, idx) => (
                          <div key={idx} className="flex justify-between">
                            <span className="text-muted-foreground">{item.type}:</span>
                            <span className={item.completion_rate > 50 ? 'text-green-600 font-medium' : 'text-orange-600 font-medium'}>
                              {item.completion_rate}%
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Company Performance (Job/Promotion Success Rates) */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    Company Success Rates (Job/Promotion)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {stats.by_company?.length > 0 ? (
                    <>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={stats.by_company.slice(0, 8)}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="company" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={80} />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="completed" fill="#10B981" name="Completed" />
                          <Bar dataKey="pending" fill="#F59E0B" name="Pending" />
                        </BarChart>
                      </ResponsiveContainer>
                      <div className="mt-4 pt-4 border-t">
                        <p className="text-sm font-medium mb-2">Success Rates by Company:</p>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {stats.by_company.map((item, idx) => (
                            <div key={idx} className="flex justify-between text-sm">
                              <span className="text-muted-foreground truncate">{item.company}</span>
                              <span className={item.success_rate >= 50 ? 'text-green-600 font-medium' : 'text-orange-600 font-medium'}>
                                {item.success_rate}% ({item.completed}/{item.total})
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">No job/promotion requests yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Top Blocks by Requests */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Home className="h-5 w-5" />
                    Top Blocks by Requests
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {stats.by_block?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={stats.by_block}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="block" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count" fill="#3B82F6" name="Requests">
                          {stats.by_block.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">No block data available</p>
                  )}
                </CardContent>
              </Card>

              {/* Top Constituents */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Top Requesters
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {stats.top_constituents?.length > 0 ? (
                    <div className="space-y-3">
                      {stats.top_constituents.map((constituent, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                              {idx + 1}
                            </div>
                            <div>
                              <p className="font-medium">{constituent.name}</p>
                              <p className="text-xs text-muted-foreground">Block: {constituent.block || 'N/A'}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xl font-bold text-primary">{constituent.count}</p>
                            <p className="text-xs text-muted-foreground">requests</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">No data available</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Financial Summary */}
            {stats.amount_stats?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    Financial Summary (Amounts by Request Type)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {stats.amount_stats.map((item, idx) => (
                      <div key={idx} className="p-4 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg">
                        <p className="text-sm text-emerald-700">{item.type}</p>
                        <p className="text-2xl font-bold text-emerald-900">MVR {parseFloat(item.total).toLocaleString()}</p>
                        <p className="text-xs text-emerald-600">{item.count} requests</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-medium">Total Amount:</span>
                      <span className="text-2xl font-bold text-emerald-600">
                        MVR {stats.amount_stats.reduce((sum, item) => sum + item.total, 0).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Issue Categories */}
            {stats.by_issue_category?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5" />
                    Issues by Category
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-center">
                    <ResponsiveContainer width="100%" height={250}>
                      <RechartsPieChart>
                        <Pie
                          data={stats.by_issue_category}
                          cx="50%"
                          cy="50%"
                          labelLine={true}
                          label={({ category, count, percent }) => `${category}: ${count} (${(percent * 100).toFixed(0)}%)`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="count"
                          nameKey="category"
                        >
                          {stats.by_issue_category.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* List View */}
        {viewMode === 'list' && (
          <>
        {/* Search and Filters - Mobile Optimized */}
        <Card className="mb-4 md:mb-6">
          <CardContent className="p-3 md:p-4">
            <div className="flex flex-col gap-3">
              {/* Search - Full width on mobile */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search name, ID, or address..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-10 h-12 text-base"
                  data-testid="search-input"
                />
              </div>
              
              {/* Quick Filters - Responsive */}
              <div className="flex flex-wrap gap-2">
                <Select value={filters.entry_type} onValueChange={(v) => setFilters({ ...filters, entry_type: v })}>
                  <SelectTrigger className="w-full md:w-[140px] h-10" data-testid="type-filter">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="request">Requests</SelectItem>
                    <SelectItem value="issue">Issues</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Desktop-only status filter (mobile uses toggle above) */}
                <Select 
                  value={filters.status || "all_status"} 
                  onValueChange={(v) => setFilters({ ...filters, status: v === "all_status" ? "" : v })}
                  className="hidden md:block"
                >
                  <SelectTrigger className="w-[140px] h-10 hidden md:flex" data-testid="status-filter">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_status">All Status</SelectItem>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  className={`h-10 ${showFilters ? 'bg-primary/10' : ''}`}
                  data-testid="more-filters-btn"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  Filters
                </Button>
              </div>
            </div>
            
            {/* Extended Filters */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <div>
                  <Label className="text-xs">Constituency</Label>
                  <Select 
                    value={filters.constituency_id || "all_const"} 
                    onValueChange={(v) => setFilters({ 
                      ...filters, 
                      constituency_id: v === "all_const" ? "" : v
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all_const">All Constituencies</SelectItem>
                      {constituencies.map(c => (
                        <SelectItem key={c.constituency_id} value={c.constituency_id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs">Request Type</Label>
                  <Select value={filters.request_type || "all_req"} onValueChange={(v) => setFilters({ ...filters, request_type: v === "all_req" ? "" : v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all_req">All</SelectItem>
                      {config.request_types.map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-xs">Issue Category</Label>
                  <Select value={filters.category || "all_cat"} onValueChange={(v) => setFilters({ ...filters, category: v === "all_cat" ? "" : v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all_cat">All</SelectItem>
                      {config.issue_categories.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-xs">Company</Label>
                  <Select value={filters.company || "all_company"} onValueChange={(v) => setFilters({ ...filters, company: v === "all_company" ? "" : v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all_company">All</SelectItem>
                      {config.companies.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-xs">Block</Label>
                  <Select value={filters.block || "all_block"} onValueChange={(v) => setFilters({ ...filters, block: v === "all_block" ? "" : v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all_block">All</SelectItem>
                      {blocks.map(b => (
                        <SelectItem key={b} value={b}>{b}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-xs">Date From</Label>
                  <Input
                    type="date"
                    value={filters.date_from}
                    onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                  />
                </div>
                
                <div>
                  <Label className="text-xs">Date To</Label>
                  <Input
                    type="date"
                    value={filters.date_to}
                    onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Records - Mobile Cards / Desktop Table */}
        <Card>
          <CardContent className="p-0">
            {/* Mobile Card View */}
            <div className="md:hidden">
              {loading ? (
                <div className="p-6 text-center text-muted-foreground">Loading...</div>
              ) : records.length === 0 ? (
                <div className="p-6 text-center text-muted-foreground">No records found</div>
              ) : (
                <div className="divide-y">
                  {records.map((record) => (
                    <div key={record.request_id} className="p-4 space-y-3">
                      {/* Header Row */}
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-semibold text-base">{record.constituent_name}</p>
                          <p className="text-xs text-muted-foreground">{record.constituent_id_number}</p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          record.status === 'Completed' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {record.status}
                        </span>
                      </div>
                      
                      {/* Info Row */}
                      <div className="flex flex-wrap gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(record)}`}>
                          {getTypeLabel(record)}
                        </span>
                        {record.constituent_block && (
                          <span className="px-2 py-1 bg-gray-100 rounded-full text-xs">Block {record.constituent_block}</span>
                        )}
                        {record.amount && (
                          <span className="px-2 py-1 bg-emerald-100 text-emerald-800 rounded-full text-xs">{formatAmount(record.amount)}</span>
                        )}
                      </div>

                      {/* Contact Number */}
                      {record.contact_number && (
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="h-3 w-3 text-muted-foreground" />
                          <a href={`tel:${record.contact_number}`} className="text-primary hover:underline">
                            {record.contact_number}
                          </a>
                        </div>
                      )}
                      
                      {/* Remarks */}
                      {record.remarks && (
                        <p className="text-sm text-muted-foreground line-clamp-2">{record.remarks}</p>
                      )}
                      
                      {/* Actions */}
                      <div className="flex items-center justify-between pt-2 border-t">
                        <p className="text-xs text-muted-foreground">
                          {formatDate(record.created_at)}
                        </p>
                        <div className="flex items-center gap-1">
                          {canClose && record.status === 'Pending' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-9 w-9 p-0 hover:bg-green-100"
                              onClick={() => handleQuickStatusUpdate(record.request_id, 'Completed')}
                              title="Complete"
                            >
                              <CheckCircle2 className="h-5 w-5 text-green-600" />
                            </Button>
                          )}
                          {canEdit && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-9 w-9 p-0 hover:bg-blue-100"
                              onClick={() => openEditDialog(record)}
                              title="Edit"
                            >
                              <Edit2 className="h-5 w-5 text-blue-600" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-9 w-9 p-0 hover:bg-purple-100"
                            onClick={() => handleViewHistory(record)}
                            title="History"
                          >
                            <History className="h-5 w-5 text-purple-600" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">Date</th>
                    <th className="px-4 py-3 text-left font-medium">Constituent</th>
                    <th className="px-4 py-3 text-left font-medium">Contact</th>
                    <th className="px-4 py-3 text-left font-medium">Block</th>
                    <th className="px-4 py-3 text-left font-medium">Type</th>
                    <th className="px-4 py-3 text-left font-medium">Company</th>
                    <th className="px-4 py-3 text-left font-medium">Amount</th>
                    <th className="px-4 py-3 text-left font-medium">Status</th>
                    <th className="px-4 py-3 text-left font-medium">Remarks</th>
                    <th className="px-4 py-3 text-left font-medium">Updated By</th>
                    <th className="px-4 py-3 text-center font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={11} className="px-4 py-8 text-center text-muted-foreground">
                        Loading...
                      </td>
                    </tr>
                  ) : records.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="px-4 py-8 text-center text-muted-foreground">
                        No records found
                      </td>
                    </tr>
                  ) : (
                    records.map((record) => (
                      <tr key={record.request_id} className="border-b hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap">
                          {formatDate(record.created_at)}
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium">{record.constituent_name}</p>
                            <p className="text-xs text-muted-foreground">{record.constituent_id_number}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {record.contact_number ? (
                            <a href={`tel:${record.contact_number}`} className="text-primary hover:underline text-sm">
                              {record.contact_number}
                            </a>
                          ) : '-'}
                        </td>
                        <td className="px-4 py-3">{record.constituent_block || '-'}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(record)}`}>
                            {record.entry_type === 'issue' ? '' : ''}{getTypeLabel(record)}
                          </span>
                        </td>
                        <td className="px-4 py-3">{record.company || '-'}</td>
                        <td className="px-4 py-3">{formatAmount(record.amount)}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            record.status === 'Completed' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {record.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 max-w-[200px] truncate" title={record.remarks}>
                          {record.remarks || '-'}
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-xs">{record.updated_by}</p>
                            <p className="text-xs text-muted-foreground">{formatDate(record.updated_at)}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-1">
                            {canClose && record.status === 'Pending' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0 hover:bg-green-100"
                                onClick={() => handleQuickStatusUpdate(record.request_id, 'Completed')}
                                title="Mark as Completed"
                                data-testid="complete-btn"
                              >
                                <CheckCircle2 className="h-4 w-4 text-green-600" />
                              </Button>
                            )}
                            {canEdit && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0 hover:bg-blue-100"
                                onClick={() => openEditDialog(record)}
                                title="Edit"
                                data-testid="edit-btn"
                              >
                                <Edit2 className="h-4 w-4 text-blue-600" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0 hover:bg-purple-100"
                              onClick={() => handleViewHistory(record)}
                              title="View History"
                              data-testid="history-btn"
                            >
                              <History className="h-4 w-4 text-purple-600" />
                            </Button>
                            {canDelete && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0 hover:bg-red-100"
                                onClick={() => confirmDelete(record.request_id)}
                                title="Delete"
                                data-testid="delete-btn"
                              >
                                <Trash2 className="h-4 w-4 text-red-600" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <p className="text-sm text-muted-foreground">
                Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, total)} of {total} records
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                  data-testid="prev-page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm">Page {page} of {totalPages}</span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === totalPages}
                  onClick={() => setPage(page + 1)}
                  data-testid="next-page"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
          </>
        )}

        {/* Create Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {createType === 'request' ? 'New Request' : 'New Issue'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Step 1: Select Constituency */}
              <div>
                <Label>Constituency *</Label>
                <Select 
                  value={selectedConstituencyId} 
                  onValueChange={(v) => {
                    setSelectedConstituencyId(v);
                    setSelectedConstituent(null);
                    setConstituentSearch('');
                    setConstituentResults([]);
                  }}
                >
                  <SelectTrigger data-testid="constituency-select">
                    <SelectValue placeholder="Select constituency" />
                  </SelectTrigger>
                  <SelectContent>
                    {constituencies.map(c => (
                      <SelectItem key={c.constituency_id} value={c.constituency_id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Show auto-selected campaign info */}
              {selectedConstituencyId && selectedCampaignId && (
                <div className="p-2 bg-blue-50 rounded-md text-sm">
                  <p className="text-blue-800">
                    <span className="font-medium">Campaign: </span>
                    {campaignsData.find(c => c.campaign_id === selectedCampaignId)?.name || 'Loading...'}
                    {campaignsData.find(c => c.campaign_id === selectedCampaignId)?.campaign_date && (
                      <span className="text-blue-600 ml-2">
                        (Voting: {new Date(campaignsData.find(c => c.campaign_id === selectedCampaignId)?.campaign_date).toLocaleDateString()})
                      </span>
                    )}
                  </p>
                </div>
              )}

              {/* Step 2: Search Constituent */}
              {selectedCampaignId && (
                <div>
                  <Label>Constituent *</Label>
                  <div className="relative">
                    <Input
                      placeholder="Search by name, ID, or address..."
                      value={constituentSearch}
                      onChange={(e) => setConstituentSearch(e.target.value)}
                      data-testid="constituent-search"
                    />
                    {searchingConstituent && (
                      <p className="text-xs text-muted-foreground mt-1">Searching...</p>
                    )}
                    {constituentResults.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
                        {constituentResults.map(c => (
                          <button
                            key={c.record_id}
                            className="w-full px-3 py-2 text-left hover:bg-muted text-sm"
                            onClick={() => selectConstituent(c)}
                          >
                            <p className="font-medium">{c.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {c.id_number} • {c.address} • Block {c.block_number}
                            </p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {selectedConstituent && (
                    <div className="mt-2 p-2 bg-green-50 rounded-md text-sm">
                      <p className="font-medium text-green-800">{selectedConstituent.name}</p>
                      <p className="text-green-700 text-xs">
                        {selectedConstituent.id_number} • {selectedConstituent.address} • Block {selectedConstituent.block_number}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Contact Number Field (always visible after constituent selection) */}
              {selectedConstituent && (
                <div>
                  <Label>Contact Number</Label>
                  <Input
                    type="tel"
                    placeholder="Enter contact number"
                    value={createType === 'request' ? newRequest.contact_number : newIssue.contact_number}
                    onChange={(e) => {
                      if (createType === 'request') {
                        setNewRequest({ ...newRequest, contact_number: e.target.value });
                      } else {
                        setNewIssue({ ...newIssue, contact_number: e.target.value });
                      }
                    }}
                    data-testid="contact-number-input"
                    className="h-12 text-base"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {(createType === 'request' ? newRequest.contact_number : newIssue.contact_number) !== selectedConstituent.contact_number && (createType === 'request' ? newRequest.contact_number : newIssue.contact_number)
                      ? '* This will update the constituent record'
                      : 'Auto-filled from constituent record'}
                  </p>
                </div>
              )}

              {createType === 'request' ? (
                <>
                  {/* Request Type */}
                  <div>
                    <Label>Request Type *</Label>
                    <Select 
                      value={newRequest.request_type} 
                      onValueChange={(v) => setNewRequest({ ...newRequest, request_type: v })}
                    >
                      <SelectTrigger data-testid="request-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {config.request_types.map(t => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Amount (for financial requests) */}
                  {['NSPA', 'Utilities Water', 'Utilities Electric', 'Other Donation'].includes(newRequest.request_type) && (
                    <div>
                      <Label>Amount (MVR)</Label>
                      <Input
                        type="number"
                        placeholder="Enter amount"
                        value={newRequest.amount}
                        onChange={(e) => setNewRequest({ ...newRequest, amount: e.target.value })}
                        data-testid="amount-input"
                      />
                    </div>
                  )}

                  {/* Company (for Job/Promotion) */}
                  {['New Job', 'Promotion'].includes(newRequest.request_type) && (
                    <div>
                      <Label>Company *</Label>
                      <Select 
                        value={newRequest.company} 
                        onValueChange={(v) => setNewRequest({ ...newRequest, company: v })}
                      >
                        <SelectTrigger data-testid="company-select">
                          <SelectValue placeholder="Select company" />
                        </SelectTrigger>
                        <SelectContent>
                          {config.companies.map(c => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Designation (for Promotion) */}
                  {newRequest.request_type === 'Promotion' && (
                    <>
                      <div>
                        <Label>Current Designation</Label>
                        <Input
                          placeholder="Current position"
                          value={newRequest.current_designation}
                          onChange={(e) => setNewRequest({ ...newRequest, current_designation: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Requested Designation</Label>
                        <Input
                          placeholder="Requested position"
                          value={newRequest.requested_designation}
                          onChange={(e) => setNewRequest({ ...newRequest, requested_designation: e.target.value })}
                        />
                      </div>
                    </>
                  )}

                  {/* Remarks */}
                  <div>
                    <Label>Remarks</Label>
                    <Textarea
                      placeholder="Additional notes..."
                      value={newRequest.remarks}
                      onChange={(e) => setNewRequest({ ...newRequest, remarks: e.target.value })}
                      data-testid="remarks-input"
                    />
                  </div>
                </>
              ) : (
                <>
                  {/* Issue Category */}
                  <div>
                    <Label>Category *</Label>
                    <Select 
                      value={newIssue.category} 
                      onValueChange={(v) => setNewIssue({ ...newIssue, category: v })}
                    >
                      <SelectTrigger data-testid="category-select">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {config.issue_categories.map(c => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Description */}
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      placeholder="Describe the issue..."
                      value={newIssue.description}
                      onChange={(e) => setNewIssue({ ...newIssue, description: e.target.value })}
                      data-testid="description-input"
                    />
                  </div>

                  {/* Remarks */}
                  <div>
                    <Label>Remarks</Label>
                    <Textarea
                      placeholder="Additional notes..."
                      value={newIssue.remarks}
                      onChange={(e) => setNewIssue({ ...newIssue, remarks: e.target.value })}
                    />
                  </div>
                </>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
              <Button 
                onClick={createType === 'request' ? handleCreateRequest : handleCreateIssue}
                data-testid="submit-create"
              >
                Create {createType === 'request' ? 'Request' : 'Issue'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                Edit {editingRecord?.entry_type === 'issue' ? 'Issue' : 'Request'}
              </DialogTitle>
            </DialogHeader>
            
            {editingRecord && (
              <div className="space-y-4 py-4">
                {/* Constituent Info (read-only) */}
                <div className="p-3 bg-muted rounded-md">
                  <p className="font-medium">{editingRecord.constituent_name}</p>
                  <p className="text-sm text-muted-foreground">
                    {editingRecord.constituent_id_number} • {editingRecord.constituent_address}
                  </p>
                </div>

                {editingRecord.entry_type === 'request' ? (
                  <>
                    {/* Amount */}
                    {['NSPA', 'Utilities Water', 'Utilities Electric', 'Other Donation'].includes(editingRecord.request_type) && (
                      <div>
                        <Label>Amount (MVR)</Label>
                        <Input
                          type="number"
                          value={editingRecord.amount || ''}
                          onChange={(e) => setEditingRecord({ ...editingRecord, amount: parseFloat(e.target.value) || null })}
                        />
                      </div>
                    )}

                    {/* Company */}
                    {['New Job', 'Promotion'].includes(editingRecord.request_type) && (
                      <div>
                        <Label>Company</Label>
                        <Select 
                          value={editingRecord.company || "no_company"} 
                          onValueChange={(v) => setEditingRecord({ ...editingRecord, company: v === "no_company" ? null : v })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select company" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="no_company">No company</SelectItem>
                            {config.companies.map(c => (
                              <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* Designation (for Promotion) */}
                    {editingRecord.request_type === 'Promotion' && (
                      <>
                        <div>
                          <Label>Current Designation</Label>
                          <Input
                            value={editingRecord.current_designation || ''}
                            onChange={(e) => setEditingRecord({ ...editingRecord, current_designation: e.target.value })}
                          />
                        </div>
                        <div>
                          <Label>Requested Designation</Label>
                          <Input
                            value={editingRecord.requested_designation || ''}
                            onChange={(e) => setEditingRecord({ ...editingRecord, requested_designation: e.target.value })}
                          />
                        </div>
                        <div>
                          <Label>Final/Assigned Designation</Label>
                          <Input
                            value={editingRecord.final_designation || ''}
                            onChange={(e) => setEditingRecord({ ...editingRecord, final_designation: e.target.value })}
                          />
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {/* Category */}
                    <div>
                      <Label>Category</Label>
                      <Select 
                        value={editingRecord.category || "no_cat"} 
                        onValueChange={(v) => setEditingRecord({ ...editingRecord, category: v === "no_cat" ? null : v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="no_cat">Select category</SelectItem>
                          {config.issue_categories.map(c => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Description */}
                    <div>
                      <Label>Description</Label>
                      <Textarea
                        value={editingRecord.description || ''}
                        onChange={(e) => setEditingRecord({ ...editingRecord, description: e.target.value })}
                      />
                    </div>
                  </>
                )}

                {/* Status */}
                <div>
                  <Label>Status</Label>
                  <Select 
                    value={editingRecord.status} 
                    onValueChange={(v) => setEditingRecord({ ...editingRecord, status: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Pending">Pending</SelectItem>
                      <SelectItem value="Completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Remarks */}
                <div>
                  <Label>Remarks</Label>
                  <Textarea
                    value={editingRecord.remarks || ''}
                    onChange={(e) => setEditingRecord({ ...editingRecord, remarks: e.target.value })}
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
              <Button onClick={handleUpdateRecord} data-testid="submit-edit">Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* History Dialog */}
        <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Change History</DialogTitle>
            </DialogHeader>
            
            <div className="max-h-96 overflow-y-auto">
              {historyRecords.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No history available</p>
              ) : (
                <div className="space-y-3">
                  {historyRecords.map((log, idx) => (
                    <div key={idx} className="p-3 bg-muted/50 rounded-md">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium capitalize">{log.action}</span>
                        <span className="text-xs text-muted-foreground">{formatDate(log.timestamp)}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">By: {log.user_name || 'System'}</p>
                      {log.field_changed && (
                        <p className="text-xs mt-1">
                          <span className="font-medium">{log.field_changed}:</span>{' '}
                          <span className="text-red-600">{log.old_value || 'empty'}</span>
                          {' → '}
                          <span className="text-green-600">{log.new_value || 'empty'}</span>
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button onClick={() => setShowHistoryDialog(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-red-600 flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                Confirm Delete
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              {deleteTarget === 'all' ? (
                <p className="text-center">
                  Are you sure you want to delete <strong>ALL {total} records</strong>? This action cannot be undone.
                </p>
              ) : (
                <p className="text-center">
                  Are you sure you want to delete this record? This action cannot be undone.
                </p>
              )}
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
              <Button variant="destructive" onClick={executeDelete}>
                {deleteTarget === 'all' ? 'Delete All Records' : 'Delete Record'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
